import type { BookableCandidateDto } from "@repo/api";
import { rescheduleVisit, type VisitDto } from "@repo/data-store";
import { Button } from "@repo/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@repo/ui/components/ui/dialog";
import { endOfDay, format, parseISO, startOfDay } from "date-fns";
import * as React from "react";
import { toast } from "sonner";
import { useAppForm } from "@/shared/forms/app-form";
import { useSubmitOnce } from "@/shared/forms/submit-once";
import { useFindRescheduleCandidates } from "../hooks/use-find-bookable-candidates";

interface RescheduleFormValues {
  date: Date | null;
  startTime: string;
  endTime: string;
}

const DEFAULT_VALUES: RescheduleFormValues = {
  date: null,
  startTime: "",
  endTime: "",
};

export interface RescheduleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  visit: VisitDto;
  onSuccess?: () => void;
}

/**
 * THE VISIT IS THE WHOLE INPUT. This used to re-supply the protocol, the
 * patient and a schedule id from the client, which meant a consultation — which
 * has no protocol — could not be rescheduled at all. The server resolves the
 * work off the visit, and it adds back the reservations the move will release,
 * so times overlapping the visit's current one are offered rather than hidden
 * behind the visit's own slots.
 */
export function RescheduleDialog({ open, onOpenChange, visit, onSuccess }: RescheduleDialogProps) {
  const [isRescheduling, setIsRescheduling] = React.useState(false);
  const { once } = useSubmitOnce();

  const serviceType = visit.serviceType?.display ?? visit.serviceType?.text ?? null;

  const [selectedDate, setSelectedDate] = React.useState<Date | null>(null);
  const {
    candidates,
    isLoading: isLoadingAvailability,
    error: availabilityError,
    query,
    reset,
  } = useFindRescheduleCandidates();

  React.useEffect(() => {
    if (!selectedDate || !visit.visitId) {
      reset();
      return;
    }
    void query({
      visitId: visit.visitId,
      start: startOfDay(selectedDate).toISOString(),
      end: endOfDay(selectedDate).toISOString(),
    });
  }, [selectedDate, visit.visitId, query, reset]);

  const patientName = visit.patientName ?? "Patient";

  const form = useAppForm({
    defaultValues: DEFAULT_VALUES,
    listeners: {
      onChange: ({ formApi }) => {
        const { date } = formApi.state.values;
        setSelectedDate(date);
      },
    },
    onSubmit: async ({ value }) => {
      if (!visit.visitId || !value.startTime) return;
      const candidate = candidates.find((c) => c.start === value.startTime);
      if (!candidate) {
        toast.error("Selected slot is no longer available — please pick another time.");
        return;
      }
      setIsRescheduling(true);
      try {
        await once(async (id) => {
          await rescheduleVisit({ id, visitId: visit.visitId, candidate }).isPersisted.promise;
          handleClose();
          onSuccess?.();
        });
      } catch {
      } finally {
        setIsRescheduling(false);
      }
    },
  });

  React.useEffect(() => {
    if (open) {
      setSelectedDate(null);
      form.reset(DEFAULT_VALUES);
    }
  }, [open, form]);

  function handleClose() {
    form.reset(DEFAULT_VALUES);
    onOpenChange(false);
  }

  // Each candidate is one pre-validated bookable window, already spanning the
  // whole visit — the picker maps them 1:1 rather than reassembling a span from
  // a segment list.
  const slots = candidates.map((c: BookableCandidateDto) => {
    const startD = parseISO(c.start);
    const endD = parseISO(c.end);
    return {
      startTime: c.start,
      endTime: c.end,
      dateDisplay: format(startD, "EEE, MMM d"),
      display: format(startD, "h:mm a"),
      endDisplay: format(endD, "h:mm a"),
    };
  });

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reschedule Appointment</DialogTitle>
          <DialogDescription>
            {patientName}
            {serviceType ? ` — ${serviceType}` : ""}
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
          className="space-y-4"
        >
          <form.AppField
            name="date"
            listeners={{
              onChange: () => {
                form.setFieldValue("startTime", "");
                form.setFieldValue("endTime", "");
              },
            }}
          >
            {(field) => <field.DateField label="New Date" />}
          </form.AppField>

          <form.AppField name="startTime">
            {(field) => (
              <field.AvailabilitySlotPicker
                label="Available Times"
                slots={slots}
                isLoading={isLoadingAvailability}
                error={availabilityError}
                onSlotSelect={(slot) => {
                  form.setFieldValue("endTime", slot.endTime);
                }}
              />
            )}
          </form.AppField>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isRescheduling}>
              Cancel
            </Button>
            <form.Subscribe
              selector={(s) => ({
                hasRequiredFields: !!(s.values.date && s.values.startTime && s.values.endTime),
                isPristine: s.isPristine,
              })}
            >
              {({ hasRequiredFields, isPristine }) => (
                <Button type="submit" disabled={!hasRequiredFields || isPristine || isRescheduling}>
                  {isRescheduling ? "Rescheduling..." : "Reschedule"}
                </Button>
              )}
            </form.Subscribe>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
